import React from 'react';
import {
  Box,
  FormControl,
  Select,
  SelectProps,
  Typography,
  useTheme
} from '@mui/material';

interface AttioSelectProps extends Omit<SelectProps, 'variant'> {
  label: string;
  description?: string;
  required?: boolean;
}

/**
 * Attio-style select dropdown with clean label above
 */
export const AttioSelect: React.FC<AttioSelectProps> = ({
  label,
  description,
  required = false,
  children,
  ...selectProps
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%' }}>
      {/* Label */}
      <Box sx={{ mb: 0.75 }}>
        <Typography
          component="label"
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            display: 'block',
            textAlign: 'left'
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: 'error.main',
                ml: 0.5,
                fontSize: '13px'
              }}
            >
              *
            </Typography>
          )}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.text.secondary,
              mt: 0.5,
              lineHeight: 1.4
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* Select Dropdown */}
      <FormControl fullWidth>
        <Select
          {...selectProps}
          required={required}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            backgroundColor: theme.palette.background.paper,
            fontSize: '14px',
            '& fieldset': {
              borderColor: theme.palette.divider
            },
            '&:hover fieldset': {
              borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1px'
            },
            '& .MuiSelect-select': {
              py: 1.25,
              px: 1.75,
              fontWeight: 400,
              textAlign: 'left'
            },
            ...selectProps.sx
          }}
        >
          {children}
        </Select>
      </FormControl>
    </Box>
  );
};

